<template>
  <div class="bill-list">
    <!-- ===== 头部（复用全局 .customer-header-*，与销售/财务/市场三端 1:1） ===== -->
    <div class="customer-header" ref="headEl">
      <div class="customer-header__top">
        <div class="customer-header__left" @click="showBack && $emit('back')">
          <span class="bill-back" v-if="showBack">‹</span>
          <span class="customer-header__title">{{ headTitle }}</span>
        </div>
        <div class="customer-header__search">
          <el-input
            v-model="keyword"
            class="search-input"
            :placeholder="searchPlaceholder"
            clearable
            @keyup.enter.native="onSearchChange"
            @clear="onSearchChange"
            @change="onSearchChange"
          >
            <i slot="prefix" class="el-icon-search"></i>
          </el-input>
        </div>
        <div class="customer-header__actions">
          <div class="bill-fbtn" :class="{ on: filterOn }" @click="openFilter">
            <van-icon name="filter-o" />
            <span>筛选<i v-if="filterOn" class="bill-fbtn__dot"></i></span>
          </div>
        </div>
      </div>
      <div class="customer-header__tabs" v-if="statusTabs.length">
        <span
          v-for="t in statusTabs"
          :key="String(t.value)"
          class="customer-header__tab"
          :class="{ 'customer-header__tab--active': String(status) === String(t.value) }"
          @click="setStatus(t.value)"
        >{{ t.label }}</span>
      </div>
    </div>
    <div class="customer-header-spacer" :style="{ height: headH + 'px' }"></div>

    <!-- ===== 已生效筛选条 ===== -->
    <div class="filter-status-bar" v-if="activeLabels.length">
      <span class="filter-status-bar__txt">筛选：{{ activeLabels.join(' · ') }}</span>
      <span class="filter-status-bar__clear" @click="clearFilter">清空</span>
    </div>

    <!-- ===== 合计条（8 类一致）：共 N 条 · 金额合计 ===== -->
    <!-- 【P205-1.2.004】会议室订单独立卡片样式下不展示统计条（与设计稿 1:1） -->
    <div class="bill-sum" v-if="!roomCard">
      <div class="bill-sum__cell">
        <span class="bill-sum__lb">{{ headTitle }}条数</span>
        <span class="bill-sum__nm">{{ total }}</span>
      </div>
      <span class="bill-sum__split" v-if="hasAmount"></span>
      <div class="bill-sum__cell bill-sum__cell--amt" v-if="hasAmount">
        <span class="bill-sum__lb">金额合计</span>
        <span class="bill-sum__nm">¥{{ fmtMoney(sumAmount) }}</span>
      </div>
    </div>

    <!-- ===== 批量条（可选） ===== -->
    <div class="bill-batchbar" v-if="batchMode">
      <span class="bill-batchbar__txt">已选 {{ checked.length }} 条</span>
      <div class="btn-action" @click="exitBatch"><span>取消</span></div>
      <div class="btn-action btn-action--danger" v-if="!readonly" @click="emitBatch('delete')"><span>批量删除</span></div>
      <div class="btn-action btn-action--primary" v-if="!readonly" @click="emitBatch('edit')"><span>批量编辑</span></div>
    </div>

    <!-- ===== 列表 ===== -->
    <van-pull-refresh v-model="refreshing" @refresh="onRefresh">
      <div class="list-box">
        <van-list
          v-model:loading="loading"
          :finished="finished"
          :finished-text="rows.length ? '没有更多了' : ''"
          :immediate-check="true"
          :error.sync="listError"
          error-text="加载失败，点击重试"
          @load="onLoad"
        >
          <!-- 【P205-1.2.004】会议室订单独立卡片（快捷入口专用，1:1 复刻设计稿） -->
          <template v-if="roomCard">
            <div
              class="crm-item room-card"
              v-for="(item, i) in rows"
              :key="'rc-' + (item.sn || i)"
            >
              <div class="room-card__head">
                <span class="room-card__title">{{ dash(item.name) }}</span>
                <span class="room-card__amt" v-if="roomAmt(item)">{{ roomAmt(item) }}</span>
                <span class="room-card__st" :class="roomStCls(item)" v-if="roomStTxt(item)">
                  <i class="room-card__dot"></i>{{ roomStTxt(item) }}
                </span>
              </div>
              <div class="room-card__row room-card__row--ap" v-if="item.approval_status_name">
                <span class="room-card__ap" :class="roomStCls(item)">
                  <i class="room-card__dot"></i>审核状态: {{ item.approval_status_name }}
                </span>
              </div>
              <div class="room-card__row room-card__row--no">
                编号: {{ dash(item.serial_number) }} · {{ day(item.on_site_time) }}
              </div>
              <div class="room-card__row room-card__row--muted">
                所属客户: {{ dash(item.customer_name) }} · 所属商机: {{ dash(item.opportunitie_name || item.opportunities_name) }}
              </div>
              <div class="room-card__ft">
                <div class="room-card__btn room-card__btn--primary" v-if="canEditRow(item)" @click.stop="$emit('edit', item)">
                  <van-icon name="edit" />编辑
                </div>
                <div class="room-card__btn room-card__btn--danger" v-if="canDeleteRow(item)" @click.stop="$emit('delete', item)">
                  <van-icon name="delete-o" />删除
                </div>
              </div>
            </div>
          </template>

          <div
            class="crm-item bill-card"
            v-else
            :class="[
              { 'batch-on': batchMode },
              'bill-card--' + (view(item).tone || 'gray')
            ]"
            v-for="(item, i) in rows"
            :key="item.sn || i"
          >
            <div
              class="bill-check"
              v-if="batchMode"
              :class="{ on: checked.indexOf(item.sn) >= 0 }"
              @click.stop="toggleCheck(item)"
            >
              <van-icon name="success" />
            </div>
            <div class="item-head customer-card" @click="openDetail(item)">
              <div class="customer-card__main">
                <div class="customer-card__content">
                  <div class="customer-card__header">
                    <div class="customer-card__name-wrap">
                      <span class="customer-card__name">{{ view(item).title }}</span>
                      <!-- 【P95】三.2.01 合同类型高亮标签（支付单） -->
                      <span class="bill-tag" :class="view(item).ptagClass" v-if="view(item).ptag">{{ view(item).ptag }}</span>
                    </div>
                    <span
                      class="status-tag"
                      :class="view(item).statusClass"
                      v-if="view(item).status"
                    >{{ view(item).status }}</span>
                  </div>
                  <div class="bill-card__amt" :class="view(item).amtClass" v-if="view(item).amount">{{ view(item).amount }}</div>
                  <!-- 【P227】会议室名称单独一行、14px 加粗（会议室订单专属） -->
                  <div class="customer-card__row customer-card__row--room" v-if="view(item).roomLine">{{ view(item).roomLine }}</div>
                  <div class="customer-card__row" v-if="view(item).row1">{{ view(item).row1 }}</div>
                  <div class="customer-card__row customer-card__row--muted" v-if="view(item).row2">{{ view(item).row2 }}</div>
                </div>
              </div>
              <div class="customer-card__actions">
                <template v-if="!readonly">
                  <div class="btn-action btn-action--secondary" @click.stop="openDetail(item)"><span>详情</span></div>
                  <div class="btn-action btn-action--primary" v-if="canEditRow(item)" @click.stop="$emit('edit', item)"><span>编辑</span></div>
                  <div class="btn-action btn-action--danger" v-if="canDeleteRow(item)" @click.stop="$emit('delete', item)"><span>删除</span></div>
                </template>
                <!-- 【P95】三.3.03 「更多」展开（只读端也可用；与房态会议室列表的「▼ 更多」同款） -->
                <div class="bill-more-toggle" v-if="moreRows(item).length" @click.stop="toggleMore(item)">
                  {{ moreOpen[item.sn] ? '收起 ▲' : '更多 ▼' }}
                </div>
              </div>
            </div>
            <!-- 【P95】三.3.03 展开区：次要字段 -->
            <div v-if="moreOpen[item.sn]" class="bill-more">
              <div class="bill-more__row" v-for="(r, ri) in moreRows(item)" :key="'mr-' + ri">
                <span class="bill-more__lb">{{ r.label }}</span>
                <span class="bill-more__vl">{{ r.value }}</span>
              </div>
            </div>
          </div>
        </van-list>
        <div class="bill-empty" :class="{ 'bill-empty--lock': readonly }" v-if="!loading && !rows.length">
          <van-icon name="records" class="bill-empty__icon" />
          <div class="bill-empty__text">暂无{{ headTitle }}</div>
          <div class="bill-empty__hint">{{ emptyHint }}</div>
        </div>
      </div>
    </van-pull-refresh>

    <!-- ===== 详情弹层（8 类共用同一套分组渲染） ===== -->
    <van-popup v-model="detailShow" position="bottom" :style="{ height: '88%' }" round>
      <div class="bill-sheet">
        <div class="bill-sheet__hd">
          <span class="bill-sheet__title">{{ headTitle }}详情</span>
          <van-icon name="cross" class="bill-sheet__close" @click="detailShow = false" />
        </div>
        <div class="bill-sheet__bd">
          <div class="bill-sheet__loading" v-if="!detailGroups.length && !detailPlans.length">暂无可展示字段</div>
          <div class="bill-group" v-for="g in detailGroups" :key="g.title">
            <div class="bill-group__hd">{{ g.title }}</div>
            <div class="bill-field" v-for="f in g.fields" :key="f.label">
              <span class="bill-label">{{ f.label }}</span>
              <!-- 【P214】图片凭证：缩略图 + 点击查看大图 -->
              <img v-if="f.isImage" class="bill-img-thumb" :src="f.value" @click="previewImage(f.value)" />
              <span v-else class="bill-val" :class="{ 'bill-val--ph': f.value === '—', 'bill-val--strong': f.strong }">{{ f.value }}</span>
            </div>
          </div>
          <!-- 【P97】三.3.05 回款计划（单独成组；此前被当普通字段渲染成「—」而看不到） -->
          <div class="bill-group" v-if="detailPlans.length">
            <div class="bill-group__hd">回款计划</div>
            <div class="bill-field" v-for="(p, pi) in detailPlans" :key="'dp-' + pi">
              <span class="bill-label">{{ p.date || '—' }}</span>
              <span class="bill-val bill-val--strong">¥{{ fmtMoney(p.amount) }}</span>
            </div>
          </div>
        </div>
        <div class="bill-sheet__ft">
          <div class="btn-action btn-action--secondary" @click="detailShow = false"><span>关闭</span></div>
          <!-- 【P93】运营动作位：仅会议室订单有意义（一级推送 + 完成服务闭环） -->
          <template v-if="opsMode && billType === 'room'">
            <div class="btn-action btn-action--secondary" v-if="detailSvcStatus < 1" @click="$emit('pushtodo', cur)"><span>推送待办</span></div>
            <div class="btn-action btn-action--primary" v-if="detailSvcStatus < 3" @click="$emit('finish', cur)"><span>完成服务</span></div>
            <div class="btn-action btn-action--secondary" v-else><span>服务已完成</span></div>
          </template>
          <div class="btn-action btn-action--primary" v-else-if="canEditRow(cur)" @click="editFromDetail"><span>编辑</span></div>
        </div>
      </div>
    </van-popup>

    <!-- ===== 筛选弹层（按单据类型动态渲染） ===== -->
    <van-popup v-model="filterShow" position="bottom" :style="{ height: '78%' }" round>
      <div class="bill-filter">
        <div class="bill-filter__hd">
          <span class="bill-filter__title">筛选{{ headTitle }}</span>
          <van-icon name="cross" class="bill-sheet__close" @click="filterShow = false" />
        </div>
        <div class="bill-filter__bd">
          <template v-if="visibleFilters.length">
            <div class="bill-filter__group" v-for="f in visibleFilters" :key="f.prop">
              <div class="bill-filter__label">
                <span>{{ f.label }}</span>
                <span class="bill-filter__reset" v-if="draftOn(f)" @click="clearDraft(f)">清除</span>
              </div>

              <!-- ① 日期区间：滚动选择 -->
              <div class="bill-filter__range" v-if="f.type === 'daterange'">
                <div
                  class="bill-filter__pick"
                  :class="{ 'bill-filter__pick--on': !!draft[dk(f, 'begin')] }"
                  @click="openDate(dk(f, 'begin'))"
                >{{ draft[dk(f, 'begin')] || '开始日期' }}</div>
                <span class="bill-filter__sep">~</span>
                <div
                  class="bill-filter__pick"
                  :class="{ 'bill-filter__pick--on': !!draft[dk(f, 'end')] }"
                  @click="openDate(dk(f, 'end'))"
                >{{ draft[dk(f, 'end')] || '结束日期' }}</div>
              </div>

              <!-- ② 金额区间 -->
              <div class="bill-filter__range" v-else-if="f.type === 'amountRange'">
                <input
                  class="bill-filter__input"
                  type="number" inputmode="decimal"
                  :value="draft.amount_begin"
                  placeholder="最低金额"
                  @input="setDraft('amount_begin', $event.target.value)"
                />
                <span class="bill-filter__sep">~</span>
                <input
                  class="bill-filter__input"
                  type="number" inputmode="decimal"
                  :value="draft.amount_end"
                  placeholder="最高金额"
                  @input="setDraft('amount_end', $event.target.value)"
                />
              </div>

              <!-- ③ 可搜索选择器（客户 / 商机 / 销售合同 / 财务合同） -->
              <div
                class="bill-filter__pick bill-filter__pick--full"
                v-else-if="f.type === 'remote'"
                :class="{ 'bill-filter__pick--on': !!draft[f.prop] }"
                @click="openRemote(f)"
              >
                <span class="bill-filter__picktx">{{ remoteText(f) || (f.placeholder || '全部') }}</span>
                <van-icon name="arrow" class="bill-filter__arrow" />
              </div>

              <!-- ④ 关键字 -->
              <input
                class="bill-filter__input"
                v-else-if="f.type === 'search'"
                :value="draft.searchText"
                :placeholder="f.placeholder || '请输入关键字'"
                @input="setDraft('searchText', $event.target.value)"
              />

              <!-- ⑤ 枚举 chips（默认「全部」） -->
              <div class="bill-filter__chips" v-else>
                <span
                  class="bill-filter__chip"
                  :class="{ 'bill-filter__chip--on': !draftOn(f) }"
                  @click="setDraft(f.prop, '')"
                >全部</span>
                <span
                  class="bill-filter__chip"
                  v-for="o in optionsOf(f)"
                  :key="String(o.value)"
                  :class="{ 'bill-filter__chip--on': String(draft[f.prop]) === String(o.value) }"
                  @click="setDraft(f.prop, o.value)"
                >{{ o.label || o.text }}</span>
              </div>
            </div>
          </template>
          <div class="bill-sheet__loading" v-else>该单据暂无可筛选维度</div>
        </div>
        <div class="bill-filter__ft">
          <div class="btn-action btn-action--secondary" @click="resetFilter"><span>重置</span></div>
          <div class="btn-action btn-action--primary" @click="applyFilter"><span>确定</span></div>
        </div>
      </div>
    </van-popup>

    <!-- ===== 日期滚轮（四.1.001） ===== -->
    <van-popup v-model="dateShow" position="bottom" round>
      <van-datetime-picker
        type="date"
        :title="dateTitle"
        :value="dateCur"
        :min-date="dateMin"
        :max-date="dateMax"
        @cancel="dateShow = false"
        @confirm="onDateConfirm"
      />
    </van-popup>

    <!-- ===== 可搜索选择器弹层（四.1.006） ===== -->
    <van-popup v-model="remoteShow" position="bottom" :style="{ height: '72%' }" round>
      <div class="bill-picker">
        <div class="bill-picker__hd">
          <span class="bill-picker__title">{{ remoteTitle }}</span>
          <van-icon name="cross" class="bill-sheet__close" @click="remoteShow = false" />
        </div>
        <div class="bill-picker__search">
          <div class="bill-picker__kw">
            <van-icon name="search" class="bill-picker__kwico" />
            <input
              class="bill-picker__kwin"
              v-model="remoteKeyword"
              :placeholder="remotePlaceholder"
              @input="onRemoteInput"
            />
            <van-icon name="clear" class="bill-picker__kwclr" v-if="remoteKeyword" @click="clearRemoteKw" />
          </div>
        </div>
        <div class="bill-picker__bd">
          <div
            class="bill-picker__item"
            :class="{ 'bill-picker__item--on': !draft[remoteProp] }"
            @click="pickRemote('')"
          >
            <span class="bill-picker__nm">全部{{ remoteTitle }}</span>
          </div>
          <div
            class="bill-picker__item"
            v-for="o in remoteList"
            :key="o.value"
            :class="{ 'bill-picker__item--on': String(draft[remoteProp]) === String(o.value) }"
            @click="pickRemote(o.value)"
          >
            <span class="bill-picker__nm">{{ o.text }}</span>
            <span class="bill-picker__sub" v-if="o.sub">{{ o.sub }}</span>
          </div>
          <div class="bill-sheet__loading" v-if="remoteLoading">加载中…</div>
          <div class="bill-sheet__loading" v-else-if="!remoteList.length">无匹配结果</div>
        </div>
      </div>
    </van-popup>
  </div>
</template>

<script>
/* ============================================================
 * BillList.vue —— 全站单据列表统一组件（8 类共用）
 *   会议室订单 room / 增值业务订单 value / 支付订单 payment / 回款订单 collection
 *   余额明细 balance / 财务合同 contract / 财务发票 invoice / 退款单 refund
 *
 * 解决的核心问题：
 *   ① 合同详情内（scope=contract）与快捷入口（scope=self|dept|all）**同一套骨架**；
 *   ② 补齐快捷入口缺的：搜索框 / 共 N 条 + 合计 / 可点开详情 / 分页；
 *   ③ 状态标签不再写死 tag-allot，按各单据状态映射配色；
 *   ④ 字段中文标签（不再把英文列名直接当标签显示）；
 *   ⑤ 三端（销售 / 财务 / 市场）共用，仅 apiPrefix 与 scope 不同。
 *
 * 【P95-B4】本轮迭代（快捷入口 7 项）：
 *   四.1.02  筛选弹层「点不动」根因修复：draft[f.prop] = v 在 Vue2 里是非响应式新增属性，
 *            改走 setDraft() → $set；日期/文本输入同理改为 :value + @input。
 *   四.1.001 支付时间等日期筛选改为 vant 滚轮（van-datetime-picker）。
 *   四.1.002 支付方式/类型/状态默认「全部」（chips 首项「全部」+ draftOn 判定）。
 *   四.1.003 金额区间筛选（amount_begin / amount_end）。
 *   四.1.004 合计条重做为「条数 / 金额合计」双栏卡片。
 *   四.1.005 行级 can_edit / can_delete（后端下发），编辑/删除按钮按行渲染。
 *   四.1.006 客户 / 商机 / 销售合同 / 财务合同 四个可搜索选择器。
 * ============================================================ */
import { ImagePreview } from "vant";
import { billConfigs } from '../pages/contract/index.js'
import {
  getBillListData,
  getBillSumData,
  getBillTypesData,
  getFinanceBillListData,
  getFinanceBillSumData,
  getFinanceBillTypesData,
  getMarketBillListData,
  getMarketBillSumData,
  getMarketBillTypesData,
  getOperationBillListData,
  getOperationBillSumData,
  getOperationBillTypesData,
  getBillFilterOptionsData
} from '../api/api.js'
import { getPaymentOwnerOptions } from '../api/api.js'

/* 单据配置 key（前端 src/pages/contract/*.js）—— 复用其 fields/titleFields/filters，
   保证「合同详情内」与「快捷入口」字段口径完全一致 */
const CFG_KEY = {
  room: 'room',
  value: 'added',
  payment: 'payment',
  collection: 'collection',
  balance: 'balance',
  contract: 'financeContract',
  invoice: 'invoice',
  refund: 'refund'
}
const CONFIG = {}
billConfigs.forEach(c => { CONFIG[c.key] = c })

/* 【P93】外键列 → 展示名列 别名表
   详情弹层的字段 spec 用的是外键列（xxx_sn / owner_id），后端 BillListService::enrich()
   已把名称富化到 *_name（列表卡片已在用同一口径）。此处补一层映射，
   避免「合同 / 所属客户 / 所属商机 / 所属门店 / 会议室」在只读详情里只显示 32 位 SN。 */
const SN_NAME_ALIAS = {
  customer_sn: ['customer_name'],
  contracts_sn: ['contracts_name'],
  contract_sn: ['contracts_name'],
  opportunitie_sn: ['opportunities_name', 'opportunities_title'],
  opportunities_sn: ['opportunities_name', 'opportunities_title'],
  stores_sn: ['store_name', 'stores_name'],
  store_sn: ['store_name', 'stores_name'],
  meeting_room_sn: ['meeting_room_name'],
  suppliers_sn: ['suppliers_name'],
  company_sn: ['company_name'],
  industry_sn: ['industry_name'],
  value_added_servicses_sn: ['value_added_services_name'],
  parent_sn: ['rel_name', 'order_name'],
  order_sn: ['order_name'],
  owner_id: ['owner_name'],
  create_user: ['create_user_name'],
  update_user: ['update_user_name'],
  store_manager_sn: ['store_manager_name']
}

/* 状态 TAB（room/value 无状态机；balance 用类型区分不做 tab） */
const STATUS_TABS = {
  payment: [
    { value: '', label: '全部' },
    { value: 1, label: '审核通过' },
    { value: 0, label: '审核中' },
    { value: 2, label: '审核驳回' },
    { value: 3, label: '待提交' }
  ],
  collection: [
    { value: '', label: '全部' },
    { value: 0, label: '未回款' },
    { value: 1, label: '部分回款' },
    { value: 2, label: '已回款' }
  ],
  contract: [
    { value: '', label: '全部' },
    { value: 1, label: '生效' },
    { value: 0, label: '草稿' },
    { value: 2, label: '作废' }
  ],
  invoice: [
    { value: '', label: '全部' },
    { value: 1, label: '已开票' },
    { value: 0, label: '未开票' },
    { value: 2, label: '已作废' }
  ],
  refund: [
    { value: '', label: '全部' },
    { value: 1, label: '已退款' },
    { value: 0, label: '审核中' },
    { value: 2, label: '已驳回' },
    { value: 3, label: '草稿' }
  ],
  room: [],
  value: [],
  balance: []
}

/* 状态配色类（复用全局 .status-tag 的各种 tag-* 变体） */
const TAG_CLASS = {
  payment: { 1: 'tag-allot', 0: 'tag-visited', 2: 'tag-refuse', 3: 'tag-visited' },
  collection: { 0: 'tag-refuse', 1: 'tag-allot', 2: 'tag-ok' },
  contract: { 0: 'tag-visited', 1: 'tag-ok', 2: 'tag-refuse' },
  invoice: { 0: 'tag-visited', 1: 'tag-ok', 2: 'tag-refuse' },
  refund: { 0: 'tag-visited', 1: 'tag-ok', 2: 'tag-refuse', 3: 'tag-visited' },
  room: {},
  value: {},
  balance: {}
}

/* 【P95-B4 四.1.005】卡片左侧状态色条色调 */
const TONE_CLASS = {
  payment: { 0: 'blue', 1: 'green', 2: 'red', 3: 'gray' },
  collection: { 0: 'red', 1: 'blue', 2: 'green' },
  contract: { 0: 'gray', 1: 'green', 2: 'red' },
  invoice: { 0: 'gray', 1: 'green', 2: 'red' },
  refund: { 0: 'blue', 1: 'green', 2: 'red', 3: 'gray' }
}

const SEARCH_PH = {
  room: '搜索客户/门店/会议室',
  value: '搜索客户/门店/增值服务',
  payment: '搜索客户/单号/凭证号',
  collection: '搜索客户/单号',
  balance: '搜索客户/单号',
  contract: '搜索客户/合同名称/编号',
  invoice: '搜索客户/发票抬头/编号',
  refund: '搜索客户/单号'
}

function money(n) {
  const v = Number(n)
  if (!isFinite(v)) return '0'
  return v.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}
function dash(v) {
  if (v === 0 || v === '0') return '0'
  return (v === undefined || v === null || v === '') ? '—' : String(v)
}
function day(v) {
  return (v === undefined || v === null || v === '') ? '—' : String(v).slice(0, 10)
}
function ymd(d) {
  if (!d) return ''
  if (typeof d === 'string') return d.slice(0, 10)
  const p = (n) => (n < 10 ? '0' + n : '' + n)
  return d.getFullYear() + '-' + p(d.getMonth() + 1) + '-' + p(d.getDate())
}

export default {
  name: 'BillList',
  props: {
    /** 单据类型：room/value/payment/collection/balance/contract/invoice/refund */
    billType: { type: String, required: true },
    /** 数据范围：contract(指定合同) / self(我创建的) / dept(本部门) / all(全部) */
    scope: { type: String, default: 'self' },
    /** scope=contract 时的合同 sn */
    contractSn: { type: String, default: '' },
    /** 标题（默认取单据配置 title） */
    title: { type: String, default: '' },
    /** 只读（余额明细 / 市场端） */
    readonly: { type: Boolean, default: false },
    /** 接口前缀：client / finance / market / operation */
    apiPrefix: { type: String, default: 'client' },
    /** 每页条数 */
    pageSize: { type: Number, default: 10 },
    /** 是否显示合计金额 */
    showAmount: { type: Boolean, default: true },
    /** 是否允许编辑（快捷入口等无合同上下文的场景可关闭，仅保留详情/删除） */
    allowEdit: { type: Boolean, default: true },
    /** 【P93】运营模式：详情弹层增加「推送待办 / 完成服务」两个运营动作位 */
    opsMode: { type: Boolean, default: false },
    /** 【P169-B】金额脱敏：运营人员不参与销售金额核算，卡片金额 / 合计金额 /
     *  详情金额 / 回款计划金额 / 金额区间筛选 全部隐去。默认随 opsMode 开启。 */
    maskAmount: { type: Boolean, default: null },
    /** 【P205-1.2.004】会议室订单独立卡片样式（仅销售端「快捷入口 → 会议室订单」启用）：
     *  卡片改为「标题 + 金额胶囊 + 状态胶囊 / 审核状态 / 编号·日期 / 所属客户·所属商机 /
     *  编辑·删除两个等宽大按钮」，并隐藏顶部「条数」统计条与「详情 / 更多」。 */
    roomCard: { type: Boolean, default: false },
    /** 【P221】是否显示头部返回箭头。
     *  true  = 二级子页形态（销售/财务/市场端从合同详情点进来，需要返回）
     *  false = 内联 Tab 形态（运营端 Tab1/2 直接铺在首页内，底部 Tab 栏本身就是出口，
     *          再显示返回箭头会误导用户以为还能再退一层） */
    showBack: { type: Boolean, default: true }
  },
  data() {
    return {
      rows: [],
      total: 0,
      sumAmount: 0,
      page: 0,
      loading: false,
      finished: false,
      listError: false,
      refreshing: false,
      keyword: '',
      status: '',
      filters: {},
      draft: {},
      filterShow: false,
      /** 单据字典（来自 billTypes.maps）：{ payment: { payment_method: [{value,label}] } } */
      mapsDict: {},
      /** 受理人等需要单独接口的筛选项 */
      extraOptions: {},
      /** 【P95-B4 四.1.006】可搜索选择器远程选项：{ prop: [{value,text,sub}] } */
      remoteOptions: {},
      remoteShow: false,
      remoteProp: '',
      remoteTitle: '',
      remoteKind: '',
      remotePlaceholder: '搜索',
      remoteKeyword: '',
      remoteLoading: false,
      /** 【P95-B4 四.1.001】日期滚轮 */
      dateShow: false,
      dateKey: '',
      dateTitle: '选择日期',
      dateCur: new Date(),
      dateMin: new Date(2018, 0, 1),
      dateMax: new Date(2032, 11, 31),
      detailShow: false,
      cur: null,
      /** 【P95】三.3.03 卡片「更多」展开态（按行 sn 记录） */
      moreOpen: {},
      batchMode: false,
      checked: [],
      /** 【P93】固定头部实测高度 → 用于 .customer-header-spacer 占位。
       *  .customer-header 是 position:fixed，宿主页（client/market/finance）各自用
       *  inline style 撑 spacer；本组件独立插入时没有那个机制，
       *  缺占位会让「合计条 + 状态 Tab + 首条卡片」被头部遮住。 */
      headH: 0
    }
  },
  computed: {
    cfg() {
      return CONFIG[CFG_KEY[this.billType]] || null
    },
    headTitle() {
      if (this.title) return this.title
      return this.cfg ? this.cfg.title : '单据列表'
    },
    /** 【P95-B4】筛选维度：remote 类（需要 client/billFilterOptions）仅在销售端可用 */
    visibleFilters() {
      const fs = (this.cfg && this.cfg.filters) ? this.cfg.filters : []
      /* 【P169-B】金额脱敏：金额区间筛选一并隐去（否则可通过筛反推金额区间） */
      const base = this.masked ? fs.filter(f => f.type !== 'amountRange') : fs
      if (this.apiPrefix === 'client') return base
      return base.filter(f => f.type !== 'remote')
    },
    /** 【P95-B4 四.1.006】当前打开的可搜索选择器选项列表
     *  ⚠️ 必须由 computed 派生：模板里用了 remoteList.length，
     *  若只在 data 声明数组、由 loadRemote 异步 $set 到 remoteOptions 上，
     *  一旦 data 漏声明 remoteList 就会在白屏（Cannot read property 'length' of undefined）。 */
    remoteList() {
      return (this.remoteOptions && this.remoteOptions[this.remoteProp]) || []
    },
    /** 【P93】运营模式：当前详情行的服务状态（0未推送 1待店长确认 2店长已确认 3已完成） */
    detailSvcStatus() {
      const r = this.cur || {}
      const v = (r.service_status === undefined || r.service_status === null || r.service_status === '')
        ? 0 : Number(r.service_status)
      return isNaN(v) ? 0 : v
    },
    searchPlaceholder() {
      return SEARCH_PH[this.billType] || '搜索'
    },
    statusTabs() {
      return STATUS_TABS[this.billType] || []
    },
    hasAmount() {
      if (this.masked) return false
      return this.showAmount && this.total > 0
    },
    /** 【P169-B】是否处于金额脱敏态：显式传 maskAmount 优先，未传时跟随 opsMode */
    masked() {
      return this.maskAmount === null ? this.opsMode : !!this.maskAmount
    },
    emptyHint() {
      if (this.scope === 'contract') return '该合同下暂无此类单据'
      if (this.readonly) return this.billType === 'balance' ? '余额变动仅由系统产生' : '当前账号为只读查看'
      return '可切换筛选条件后重试'
    },
    filterOn() {
      return this.activeLabels.length > 0
    },
    activeLabels() {
      const out = []
      if (this.keyword) out.push('关键字「' + this.keyword + '」')
      if (String(this.status) !== '') {
        const t = this.statusTabs.find(x => String(x.value) === String(this.status))
        if (t) out.push(t.label)
      }
      if (this.filters.amount_begin) out.push('金额起：' + this.filters.amount_begin)
      if (this.filters.amount_end) out.push('金额止：' + this.filters.amount_end)
      const fs = (this.cfg && this.cfg.filters) ? this.cfg.filters : []
      fs.forEach(f => {
        const v = this.filters[f.prop]
        if (f.type === 'amountRange') return
        if (f.type === 'daterange') {
          const b = this.filters[f.beginKey || (f.prop + '_begin')]
          const e = this.filters[f.endKey || (f.prop + '_end')]
          if (b) out.push(f.label + '起：' + b)
          if (e) out.push(f.label + '止：' + e)
          return
        }
        if (v !== undefined && v !== null && v !== '') {
          if (f.type === 'remote') {
            const o = (this.remoteOptions[f.prop] || []).find(x => String(x.value) === String(v))
            out.push(f.label + '：' + (o ? o.text : '已选'))
          } else {
            const o = this.optionsOf(f).find(x => String(x.value) === String(v))
            out.push(f.label + '：' + (o ? (o.label || o.text) : v))
          }
        }
      })
      return out
    },
    detailGroups() {
      const cfg = this.cfg
      const row = this.cur
      if (!cfg || !row) return []
      const groups = {}
      const order = []
      ;(cfg.fields || []).forEach(f => {
        if (!f.label || f.type === 'hidden') return
        /* 【P97】三.3.05 回款计划（type=plans）不走普通字段：它的值在 row.payment_plans 数组里，
           按普通字段渲染只会显示「—」。统一交给 detailPlans 单独成组渲染。 */
        if (f.type === 'plans') return
        const g = f.group || '基本信息'
        if (!groups[g]) { groups[g] = []; order.push(g) }
        const _val = this.fieldText(row, f)
        groups[g].push({
          label: f.label,
          value: _val,
          strong: /amount|price/.test(f.prop) && f.type !== 'hidden',
          /* 【P214】upload 类型（支付单图片凭证）渲染缩略图 + 点击大图 */
          isImage: f.type === 'upload' && !!_val && _val !== '—'
        })
      })
      return order.map(k => ({ title: k, fields: groups[k] }))
    },
    /** 【P97】三.3.05 快捷入口详情里的「回款计划」行（数据源：列表接口已挂 payment_plans） */
    detailPlans() {
      const row = this.cur
      if (!row) return []
      /* 【P169-B】金额脱敏：回款计划整组不展示（月份 + 金额都是资金信息） */
      if (this.masked) return []
      const arr = row.payment_plans || row.pay_plans || []
      if (!Array.isArray(arr)) return []
      return arr
        .filter(p => p && (p.date || (p.amount !== undefined && p.amount !== null && p.amount !== '')))
        .map(p => ({
          date: p.date ? String(p.date).slice(0, 10) : '',
          amount: (p.amount === undefined || p.amount === null || p.amount === '') ? 0 : Number(p.amount)
        }))
    }
  },
  watch: {
    billType() { this.reset(true); this.measureHead() },
    scope() { this.reset(true) },
    contractSn() { this.reset(true) },
    /** 状态 Tab 出现/消失会改变头部高度，必须重测 */
    statusTabs() { this.$nextTick(this.measureHead) }
  },
  mounted() {
    this.reset(true)
    this.loadMaps()
    this.$nextTick(this.measureHead)
    // 字体/图标异步就位后再校准一次
    setTimeout(this.measureHead, 400)
  },
  methods: {
      /* 【P214】图片大图预览 */
      previewImage(url) {
        if (!url) { return; }
        const list = (Array.isArray(url) ? url : [url])
          .map(u => (typeof u === 'string' ? u : (u && (u.url || u.src)) || '')).filter(u => !!u);
        if (!list.length) { return; }
        ImagePreview(list);
      },

    fmtMoney: money,
    dash: dash,
    day: day,

    /* ============================================================
     * 【P205-1.2.004】会议室订单独立卡片（快捷入口专用）
     * ============================================================ */
    /** 金额胶囊：脱敏态或不大于 0 时不展示 */
    roomAmt(item) {
      if (this.masked) return ''
      const n = Number(item && item.amount)
      return n > 0 ? '¥' + money(n) : ''
    },
    /** 状态胶囊文案：优先后端 approval_status_name，退化为 status_name */
    roomStTxt(item) {
      const t = dash((item && (item.approval_status_name || item.status_name)) || '')
      return t === '—' ? '' : t
    },
    /** 状态胶囊配色：3 通过=绿 / 5 未通过·6 拒绝=红 / 0 未开始·2 等待审核=橙 / 其余=灰 */
    roomStCls(item) {
      const s = Number(item && (item.approval_status !== undefined && item.approval_status !== null
        ? item.approval_status : item.status))
      if (s === 3) return 'is-ok'
      if (s === 5 || s === 6) return 'is-danger'
      if (s === 0 || s === 2) return 'is-warn'
      return 'is-allot'
    },

    /* ============================================================
     * 【P95-B4 四.1.005】行级操作权限
     *   后端 BillListService::rowPerms() 下发 can_edit / can_delete（0/1），
     *   未下发时回退到「单据是否配置了 editFn/deleteFn」的老口径。
     * ============================================================ */
    canEditRow(item) {
      if (this.readonly || !this.allowEdit) return false
      if (item && item.can_edit !== undefined && item.can_edit !== null && item.can_edit !== '') {
        return Number(item.can_edit) === 1
      }
      return !!(this.cfg && this.cfg.editFn)
    },
    canDeleteRow(item) {
      if (this.readonly) return false
      if (item && item.can_delete !== undefined && item.can_delete !== null && item.can_delete !== '') {
        return Number(item.can_delete) === 1
      }
      return !!(this.cfg && this.cfg.deleteFn)
    },

    /* ============================================================
     * 【P95-B4 四.1.02】筛选草稿必须走 $set
     *   根因：draft 初始为 {}（filters 为空时 Object.assign 出来的对象没有任何筛选键），
     *   Vue2 对「实例化后新增的属性」不做响应式劫持 → 点 chips 里 draft[f.prop] = v
     *   数据其实写进去了，但视图不重渲 → 用户感受就是「筛选点不动」。
     * ============================================================ */
    setDraft(k, v) {
      this.$set(this.draft, k, v)
    },
    /** 该筛选项是否已选（用于「清除」与「全部」高亮） */
    draftOn(f) {
      if (!f) return false
      if (f.type === 'daterange') {
        return !!this.draft[this.dk(f, 'begin')] || !!this.draft[this.dk(f, 'end')]
      }
      if (f.type === 'search') return !!this.draft.searchText
      const v = this.draft[f.prop]
      return !(v === undefined || v === null || v === '')
    },
    clearDraft(f) {
      if (!f) return
      if (f.type === 'daterange') {
        this.setDraft(this.dk(f, 'begin'), '')
        this.setDraft(this.dk(f, 'end'), '')
        return
      }
      if (f.type === 'search') { this.setDraft('searchText', ''); return }
      this.setDraft(f.prop, '')
    },
    dk(f, which) {
      if (which === 'begin') return f.beginKey || (f.prop + '_begin')
      return f.endKey || (f.prop + '_end')
    },

    /* ---------- 【P95-B4 四.1.001】日期滚轮 ---------- */
    openDate(key) {
      if (!key) return
      this.dateKey = key
      const cur = this.draft[key]
      this.dateTitle = '选择日期'
      this.dateCur = cur ? new Date(String(cur).slice(0, 10).replace(/-/g, '/')) : new Date()
      this.dateShow = true
    },
    onDateConfirm(v) {
      const s = ymd(v)
      if (this.dateKey) this.setDraft(this.dateKey, s)
      this.dateShow = false
    },

    /* ---------- 【P95-B4 四.1.006】可搜索选择器 ---------- */
    remoteText(f) {
      const v = this.draft[f.prop]
      if (v === undefined || v === null || v === '') return ''
      const o = (this.remoteOptions[f.prop] || []).find(x => String(x.value) === String(v))
      return o ? o.text : '已选 1 项'
    },
    openRemote(f) {
      this.remoteProp = f.prop
      this.remoteTitle = f.label || '请选择'
      this.remoteKind = f.kind || ''
      this.remotePlaceholder = f.placeholder || ('搜索' + (f.label || ''))
      this.remoteKeyword = ''
      this.remoteShow = true
      if (!this.remoteKind) return
      /* 每次打开都重新拉一次（带当前关键字），保证选项与后端最新数据一致 */
      this.loadRemote()
    },
    onRemoteInput() {
      clearTimeout(this.__rmTimer)
      this.__rmTimer = setTimeout(() => this.loadRemote(), 280)
    },
    clearRemoteKw() {
      this.remoteKeyword = ''
      this.loadRemote()
    },
    loadRemote() {
      if (!this.remoteKind) return Promise.resolve()
      this.remoteLoading = true
      const prop = this.remoteProp
      return getBillFilterOptionsData({ kind: this.remoteKind, keyword: this.remoteKeyword }).then(res => {
        this.remoteLoading = false
        const d = (res && res.code === 200 && res.data) ? res.data : null
        const list = d && Array.isArray(d.list) ? d.list : []
        this.$set(this.remoteOptions, prop, list)
      }).catch(() => { this.remoteLoading = false })
    },
    pickRemote(v) {
      this.setDraft(this.remoteProp, v)
      this.remoteShow = false
    },

    /* 【P95】三.3.03 卡片「更多」：切换展开态 */
    toggleMore(item) {
      const sn = item.sn || ''
      const cur = !!this.moreOpen[sn]
      this.$set(this.moreOpen, sn, !cur)
    },
    /** 【P95】三.3.03 「更多」里展示的次要字段（按单据类型挑选，避开卡片上已展示的）
     *  只输出后端确实返回了值的字段，全部为空则返回 [] → 不渲染「更多」按钮。 */
    moreRows(item) {
      const t = this.billType
      const out = []
      const push = (label, v) => {
        if (v === undefined || v === null || v === '') { return }
        const sv = String(v).replace(/<[^>]*>/g, '').trim()
        if (sv === '' || sv === '—') { return }
        out.push({ label: label, value: sv })
      }
      if (t === 'room') {
        push('单号', item.serial_number)
        push('商机', item.opportunities_name)
        push('合同', item.contracts_name)
        push('创建人', item.create_user_name)
        push('备注', item.remark)
      } else if (t === 'value') {
        push('单号', item.serial_number)
        push('商机', item.opportunities_name)
        push('合同', item.contracts_name)
        push('创建人', item.create_user_name)
        push('备注', item.remark)
      } else if (t === 'payment') {
        push('单号', item.serial_number)
        push('支付类型', item.payment_type_name)
        push('销售合同', item.order_name)
        push('创建人', item.create_user_name)
        push('备注', item.remark)
      } else if (t === 'collection') {
        push('单号', item.serial_number)
        push('关联', item.rel_name)
        push('创建人', item.create_user_name)
      } else if (t === 'balance') {
        /* 【P213 需求1】列表内容补全 */
        push('操作方式', item.operation_name || item.source_name)
        push('收支类型', item.type_name)
        push('自动退款', Number(item.is_auto_refund) === 1 ? '是' : '否')
        push('金额方向', Number(item.is_income) === 1 ? '入账' : '出账')
        push('客户', item.customer_name)
        push('流水号', item.serial_number)
        push('销售合同', item.order_name)
        push('财务合同', item.contracts_name)
        push('创建人', item.create_user_name)
        push('备注', item.remark)
        push('时间', item.create_time)
      } else if (t === 'contract') {
        push('合同号', item.finance_no || item.serial_number)
        push('所属商机', item.opportunities_name)
        push('创建人', item.create_user_name)
        push('备注', item.remark)
      } else if (t === 'invoice') {
        push('发票号', item.invoice_no || item.serial_number)
        push('合同', item.contracts_name)
        push('创建人', item.create_user_name)
      } else if (t === 'refund') {
        push('退款单号', item.refund_no || item.serial_number)
        push('创建人', item.create_user_name)
        push('备注', item.remark)
      } else {
        push('单号', item.serial_number)
        push('创建人', item.create_user_name)
      }
      return out
    },

    /** 【P93】测量固定头部真实高度，写入 spacer 占位 */
    measureHead() {
      const el = this.$refs.headEl
      const h = el ? el.offsetHeight : 0
      if (h && h !== this.headH) this.headH = h
    },

    /* ---------- 单据字典（筛选/标签的选项来源；来自 billTypes.maps） ---------- */
    apiTypes(p) {
      if (this.apiPrefix === 'finance') return getFinanceBillTypesData(p)
      if (this.apiPrefix === 'market') return getMarketBillTypesData(p)
      if (this.apiPrefix === 'operation') return getOperationBillTypesData(p)
      return getBillTypesData(p)
    },
    loadMaps() {
      this.apiTypes({}).then(res => {
        const d = (res && res.code === 200 && res.data) ? res.data : null
        const types = d && Array.isArray(d.types) ? d.types : []
        const dict = {}
        types.forEach(t => {
          const m = t.maps || {}
          const one = {}
          Object.keys(m).forEach(col => {
            const src = m[col] || {}
            one[col] = Object.keys(src).map(k => ({ value: k, label: src[k] }))
          })
          dict[t.value] = one
        })
        this.mapsDict = dict
      }).catch(() => {})
      /* 受理人选项（仅支付单用，走既有 orderPaymentOwnerOptions） */
      getPaymentOwnerOptions({}).then(res => {
        const d = (res && res.code === 200 && res.data) ? res.data : null
        const list = d && (Array.isArray(d) ? d : (Array.isArray(d.list) ? d.list : null))
        if (list && list.length) {
          this.extraOptions = Object.assign({}, this.extraOptions, {
            owner: list.map(x => ({
              value: x.value !== undefined ? x.value : (x.id !== undefined ? x.id : x.staff_id),
              label: x.label || x.text || x.name
            }))
          })
        }
      }).catch(() => {})
    },

    /* ---------- 数据获取 ---------- */
    params(extra) {
      const p = Object.assign(
        {
          bill_type: this.billType,
          scope: this.scope,
          page: this.page + 1,
          pageSize: this.pageSize
        },
        this.scope === 'contract' && this.contractSn ? { sn: this.contractSn } : {},
        this.keyword ? { searchText: this.keyword } : {},
        String(this.status) !== '' ? { status: this.status } : {},
        this.filters,
        extra || {}
      )
      return p
    },
    apiList(p) {
      if (this.apiPrefix === 'finance') return getFinanceBillListData(p)
      if (this.apiPrefix === 'market') return getMarketBillListData(p)
      if (this.apiPrefix === 'operation') return getOperationBillListData(p)
      return getBillListData(p)
    },
    apiSum(p) {
      if (this.apiPrefix === 'finance') return getFinanceBillSumData(p)
      if (this.apiPrefix === 'market') return getMarketBillSumData(p)
      if (this.apiPrefix === 'operation') return getOperationBillSumData(p)
      return getBillSumData(p)
    },
    reset(fetch) {
      this.rows = []
      this.total = 0
      this.sumAmount = 0
      this.page = 0
      this.finished = false
      this.listError = false
      this.loading = false
      if (fetch) {
        this.loading = true
        this.$nextTick(() => this.fetchPage())
        this.fetchSum()
      }
    },
    fetchPage() {
      const first = this.page === 0
      const p = this.params()
      this.loading = true
      return this.apiList(p).then(res => {
        const d = (res && res.code === 200 && res.data) ? res.data : null
        const arr = d && Array.isArray(d.data) ? d.data : []
        this.rows = first ? arr : this.rows.concat(arr)
        this.total = d && d.total !== undefined ? Number(d.total) : this.rows.length
        const last = d && d.last_page ? Number(d.last_page) : 1
        this.page = d && d.current_page ? Number(d.current_page) : (this.page + 1)
        this.finished = this.page >= last || arr.length === 0
        this.loading = false
        this.refreshing = false
      }).catch(() => {
        this.loading = false
        this.refreshing = false
        this.listError = true
        this.finished = true
      })
    },
    fetchSum() {
      this.apiSum(this.params({ page: 1, pageSize: 1 })).then(res => {
        const d = (res && res.code === 200 && res.data) ? res.data : null
        if (d) {
          if (d.total !== undefined) this.total = Number(d.total)
          this.sumAmount = Number(d.amount || 0)
        }
      }).catch(() => {})
    },
    onLoad() {
      if (this.finished) { this.loading = false; return }
      this.fetchPage()
    },
    onRefresh() {
      this.rows = []
      this.page = 0
      this.finished = false
      this.fetchPage()
      this.fetchSum()
    },
    onSearchChange() {
      this.reset(true)
    },
    setStatus(v) {
      if (String(this.status) === String(v)) return
      this.status = v
      this.reset(true)
    },

    /* ---------- 筛选 ---------- */
    optionsOf(f) {
      if (f.options && f.options.length) return f.options
      const dict = this.mapsDict[this.billType] || {}
      if (f.optionsFn && dict[f.optionsFn] && dict[f.optionsFn].length) return dict[f.optionsFn]
      if (f.optionsFn && this.extraOptions[f.optionsFn]) return this.extraOptions[f.optionsFn]
      return []
    },
    openFilter() {
      this.draft = Object.assign({}, this.filters, this.keyword ? { searchText: this.keyword } : {})
      this.filterShow = true
      /* 已选的可搜索选择器：进面板时预热一次，否则只显示「已选 1 项」 */
      const fs = this.visibleFilters || []
      fs.forEach(f => {
        if (f.type === 'remote' && this.draft[f.prop]) {
          this.remoteProp = f.prop
          this.remoteKind = f.kind || ''
          this.remoteKeyword = ''
          this.loadRemote()
        }
      })
    },
    resetFilter() {
      /* 整体替换 draft 才能保证 Vue2 重新收集依赖（draft 是 data 属性，替换即响应式） */
      this.draft = {}
    },
    applyFilter() {
      const next = {}
      Object.keys(this.draft).forEach(k => {
        const v = this.draft[k]
        if (v !== undefined && v !== null && v !== '') next[k] = v
      })
      const kw = next.searchText || ''
      delete next.searchText
      this.filters = next
      this.keyword = kw
      this.filterShow = false
      this.reset(true)
    },
    clearFilter() {
      this.filters = {}
      this.keyword = ''
      this.reset(true)
    },

    /* ---------- 详情 ---------- */
    openDetail(item) {
      this.cur = item
      this.detailShow = true
    },
    editFromDetail() {
      const it = this.cur
      this.detailShow = false
      this.$emit('edit', it)
    },
    /** 【P93】详情只读值的数组归一
     *  - start_time 存 JSON 数组字符串：'["12:30","18:30"]' → '12:30 ~ 18:30'
     *  - table_types 存二维 id 数组：'[[252]]' → 打平一层后按 options 字典取名，取不到则 '252'
     *  - option_service 常为 '[]' → 返回 ''（由 dash 显示「—」）
     *  - 非数组（普通标量/纯字符串）→ 返回 null，交给上层继续走原有分支
     */
    aryText(v, sep, optMap) {
      let arr = v
      if (typeof arr === 'string') {
        const s = arr.trim()
        if (s.charAt(0) !== '[') { return null }
        try { arr = JSON.parse(s) } catch (e) { return null }
      }
      if (!Array.isArray(arr)) { return null }
      const flat = []
      arr.forEach(x => {
        if (Array.isArray(x)) { x.forEach(y => flat.push(y)) } else { flat.push(x) }
      })
      const parts = []
      flat.forEach(x => {
        if (x === null || x === undefined || x === '') { return }
        if (typeof x === 'object') { parts.push(String(x.label || x.name || x.value || '')); return }
        const mapped = optMap ? optMap[String(x)] : ''
        parts.push(mapped || String(x))
      })
      return parts.join(sep)
    },

    fieldText(row, f) {
      const prop = f.prop
      let v = row[prop]
      if (v === undefined || v === null || v === '') v = row[prop + '_name']
      /* 【P93】spec prop 形如 xxx_name 但表里只有基础列 xxx 时（如会议室时段 time_type_name），
         回落到基础列，再交给下面的 options 字典映射，否则会白显示「—」 */
      if ((v === undefined || v === null || v === '') && /_name$/.test(prop)) {
        v = row[prop.replace(/_name$/, '')]
      }
      if (v === undefined || v === null || v === '') v = f.nameKey ? row[f.nameKey] : ''
      /* 【P93】外键列优先展示后端富化出来的名称（详情不再出现 32 位 SN） */
      const alts = SN_NAME_ALIAS[prop]
      if (alts) {
        for (let i = 0; i < alts.length; i++) {
          const c = row[alts[i]]
          if (c !== undefined && c !== null && c !== '') { v = c; break }
        }
      }
      /* 【P93】数组类值归一（range / chips）
         实测原始存值：
           start_time   = '["12:30","18:30"]'  → 12:30 ~ 18:30
           table_types  = '[[252]]'            → 打平一层后按 id 或 options 字典取名
           option_service = '[]'               → 空 → 「—」
         不处理的话详情弹层会把整串原始 JSON 渲染出来。 */
      const optMap = {}
      if (f.options && f.options.length) {
        f.options.forEach(o => { optMap[String(o.value)] = o.label || o.text || '' })
      }
      const aTxt = this.aryText(v, f.type === 'range' ? ' ~ ' : '、', optMap)
      if (aTxt !== null) { return dash(aTxt) }
      if (f.type === 'select' && f.options && f.options.length) {
        const o = f.options.find(x => String(x.value) === String(v))
        if (o) v = o.label
      }
      if (/amount|price/.test(prop) && v !== undefined && v !== null && v !== '') {
        /* 【P169-B】金额脱敏：运营端详情金额也不展示 */
        if (this.masked) { return '—' }
        const n = Number(v)
        return isNaN(n) ? String(v) : '¥' + money(n)
      }
      if (f.type === 'date' || f.type === 'range') return day(v)
      return dash(v)
    },

    /* ---------- 卡片视图模型（8 类统一口径） ---------- */
    view(item) {
      const t = this.billType
      /* 【P169-B】脱敏态下卡片金额恒为空（后端也已不下发，前端兜底防缓存旧数据） */
      const amt = (n) => (this.masked ? '' : (Number(n) > 0 ? '¥' + money(n) : ''))
      const st = dash(item.status_name || '')
      const cls = (TAG_CLASS[t] && item.status !== undefined && item.status !== null)
        ? (TAG_CLASS[t][Number(item.status)] || 'tag-allot')
        : 'tag-allot'
      const tone = (TONE_CLASS[t] && item.status !== undefined && item.status !== null)
        ? (TONE_CLASS[t][Number(item.status)] || 'gray')
        : 'blue'
      const out = { title: '', amount: '', status: st === '—' ? '' : st, statusClass: cls, row1: '', row2: '', roomLine: '', tone: tone }
      if (t === 'room') {
        out.title = dash(item.name)
        out.amount = amt(item.amount)
        /* 【P227】会议室名称单独成行：优先 meeting_room_name（列表接口已带），
           退回 name（编辑回填用），都没有就不出该行。 */
        const rn = item.meeting_room_name || ''
        if (rn) out.roomLine = '会议室 ' + rn
        out.row1 = '使用时间 ' + day(item.on_site_time) + '　时段 ' + dash(item.time_type_name) + '　人数 ' + dash(item.number_people)
        out.row2 = '客户 ' + dash(item.customer_name) + '　' + dash(item.store_name) + '　单号 ' + dash(item.serial_number)
        out.status = Number(item.is_free) === 2 ? '免费' : (st === '—' ? '已预定' : st)
        out.statusClass = Number(item.is_free) === 2 ? 'tag-visited' : 'tag-ok'
        out.tone = Number(item.is_free) === 2 ? 'gray' : (Number(item.status) === 1 ? 'green' : 'blue')
      } else if (t === 'value') {
        out.title = dash(item.value_added_services_goods_name || item.name)
        out.amount = amt(item.value_added_services_amount || item.sales_amount)
        out.row1 = '客户 ' + dash(item.customer_name) + '　门店 ' + dash(item.store_name)
        out.row2 = '使用日期 ' + day(item.on_date || item.on_site_time) + '　人数 ' + dash(item.quantity || item.number_people)
      } else if (t === 'payment') {
        out.title = dash(item.name || item.title_text)
        out.amount = amt(item.amount)
        // 【P95】三.2.01 合同类型（payment_product）改为高亮标签展示，从 row1 里移除避免重复
        const PTAG_CLASS = { 1: '', 2: 'bill-tag--warn', 3: 'bill-tag--ok', 4: 'bill-tag--danger', 5: 'bill-tag--muted' }
        const __pp = Number(item.payment_product)
        out.ptag = dash(item.payment_product_name)
        out.ptagClass = (PTAG_CLASS[__pp] !== undefined) ? PTAG_CLASS[__pp] : 'bill-tag--muted'
        out.row1 = '支付时间 ' + day(item.pay_time || item.payment_date) + '　' + dash(item.payment_method_name)
        out.row2 = '客户 ' + dash(item.customer_name) + '　受理人 ' + dash(item.owner_name) + '　单号 ' + dash(item.serial_number)
      } else if (t === 'collection') {
        out.title = dash(item.rel_type_name) + '回收款'
        out.amount = amt(item.plan_amount || item.amount)
        out.row1 = '计划回款日 ' + day(item.plan_date || item.payment_date) + '　关联 ' + dash(item.rel_name)
        out.row2 = '客户 ' + dash(item.customer_name) + '　' + dash(item.store_name)
      } else if (t === 'balance') {
        /* 【P213 需求0】标题改用后端自动生成的 name（对齐支付单「客户X通过Y支付Z元」风格）。
           此前这里拼的是「充值　客户名」，信息量不足（用户反馈「太简单了不详细」）。 */
        out.title = dash(item.name || item.title)
        const __amt = Number(item.amount);
        out.amount = amt(item.amount);
        /* 负数（扣费/退款出账）标红 —— 需求明确要求 */
        out.amtClass = (!isNaN(__amt) && __amt < 0) ? 'bill-card__amt--out' : '';
        /* 操作方式（充值/退款/转款）作为状态标签；收支方向（充值/扣费）作为分类标签 */
        out.status = dash(item.operation_name || item.source_name);
        out.statusClass = Number(item.source) === 2 ? 'tag-warn'
                        : (Number(item.source) === 3 ? 'tag-allot' : 'tag-ok');
        out.ptag = dash(item.type_name);
        const __ptc = { 1: 'bill-tag--ok', 2: 'bill-tag--danger' };
        out.ptagClass = (__ptc[Number(item.type)] !== undefined) ? __ptc[Number(item.type)] : 'bill-tag--muted';
        out.tone = __amt < 0 ? 'red' : 'green';
        /* 需求要求的「自动退款」标记：退款类且系统自动入账 */
        if (Number(item.is_auto_refund) === 1) {
          out.row1 = '自动退款　' + dash(item.create_time);
        } else {
          out.row1 = dash(item.create_time);
        }
        out.row2 = '客户 ' + dash(item.customer_name)
                 + '　' + dash(item.order_name || item.contracts_name || '')
                 + (item.remark ? '　' + dash(item.remark) : '');
      } else if (t === 'contract') {
        out.title = dash(item.name)
        out.amount = amt(item.amount)
        out.row1 = '签约 ' + day(item.sign_date || item.signing_date) + '　' + dash(item.category_name) + '　' + dash(item.stage_name)
        out.row2 = '客户 ' + dash(item.customer_name) + '　合同号 ' + dash(item.finance_no || item.serial_number)
      } else if (t === 'invoice') {
        out.title = dash(item.invoice_title || item.title || item.name)
        out.amount = amt(item.amount)
        out.row1 = '开票日期 ' + day(item.invoice_date || item.signing_date) + '　购买方 ' + dash(item.buyer_name)
        out.row2 = '客户 ' + dash(item.customer_name) + '　发票号 ' + dash(item.invoice_no || item.serial_number)
      } else if (t === 'refund') {
        out.title = dash(item.name || item.title)
        out.amount = amt(item.amount)
        out.row1 = '客户 ' + dash(item.customer_name) + '　' + dash(item.store_name)
        out.row2 = '单号 ' + dash(item.refund_no || item.serial_number) + '　创建 ' + day(item.refund_date || item.create_time)
      }
      return out
    },

    /* ---------- 批量 ---------- */
    toggleCheck(item) {
      const i = this.checked.indexOf(item.sn)
      if (i >= 0) this.checked.splice(i, 1)
      else this.checked.push(item.sn)
    },
    exitBatch() {
      this.batchMode = false
      this.checked = []
    },
    emitBatch(action) {
      const items = this.rows.filter(r => this.checked.indexOf(r.sn) >= 0)
      this.$emit('batch', { action, items })
    },
    /** 供父组件开启批量模式 */
    enterBatch() {
      this.batchMode = true
      this.checked = []
    }
  }
}
</script>

<style scoped>
/* 【P213】余额明细：负数（扣费/出账）金额标红 */
.bill-card__amt--out {
  color: #e04141 !important;
  font-weight: 700;
}
.bill-back {
  font-size: 19px;
  color: #4e5969;
  line-height: 1;
  margin-right: 2px;
}
.bill-fbtn {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  font-size: 10px;
  color: #1d2129;
}
.bill-fbtn.on {
  color: #2b5fd9;
}
.bill-fbtn.on .van-icon {
  color: #2b5fd9;
}
.bill-fbtn__dot {
  position: absolute;
  top: -2px;
  right: -6px;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #e34d59;
}

/* ============================================================
 * 【P205-1.2.004】会议室订单独立卡片（快捷入口专用）
 *   1:1 复刻设计稿：标题 + 金额胶囊 + 状态胶囊 / 审核状态 / 编号·日期 /
 *   所属客户·所属商机 / 编辑·删除两个等宽大按钮。
 *   ⚠️ 仅作用于 .room-card，不影响 .bill-card 等其余 8 类单据卡片。
 * ============================================================ */
.room-card {
  padding: 14px 14px 0;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.04);
}
.room-card__head {
  display: flex;
  align-items: center;
  overflow: hidden;
}
.room-card__title {
  flex: 0 1 auto;
  min-width: 0;
  font-size: 15px;
  font-weight: 700;
  color: #1d2129;
  line-height: 22px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.room-card__amt {
  flex: none;
  margin-left: 6px;
  padding: 1px 8px;
  font-size: 12px;
  font-weight: 600;
  color: #2f6bff;
  background: #eef3ff;
  border-radius: 20px;
  line-height: 18px;
}
.room-card__st {
  flex: none;
  margin-left: auto;
  padding-left: 8px;
  display: inline-flex;
  align-items: center;
  font-size: 12px;
  font-weight: 600;
  line-height: 18px;
}
.room-card__st.is-ok { color: #17b26a; }
.room-card__st.is-danger { color: #e34d59; }
.room-card__st.is-warn { color: #ff8f1f; }
.room-card__st.is-allot { color: #86909c; }
.room-card__dot {
  width: 6px;
  height: 6px;
  margin-right: 4px;
  border-radius: 50%;
  background: currentColor;
}
.room-card__row {
  font-size: 12px;
  line-height: 19px;
  color: #1d2129;
}
.room-card__row--ap {
  margin-top: 6px;
}
/* 审核状态胶囊：浅底 + 圆点 + 文案 */
.room-card__ap {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  font-size: 12px;
  font-weight: 600;
  line-height: 18px;
  border-radius: 6px;
}
.room-card__ap.is-ok { color: #17b26a; background: #e8f8ef; }
.room-card__ap.is-danger { color: #e34d59; background: #fdeef0; }
.room-card__ap.is-warn { color: #ff8f1f; background: #fff4e6; }
.room-card__ap.is-allot { color: #86909c; background: #f2f3f5; }
.room-card__ap .room-card__dot { background: currentColor; }
.room-card__row--no {
  margin-top: 8px;
  color: #1d2129;
}
.room-card__row--muted {
  margin-top: 4px;
  color: #86909c;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
/* 按钮行：分隔线 + 两个等宽大按钮 */
.room-card__ft {
  display: flex;
  gap: 10px;
  margin-top: 12px;
  padding: 12px 0;
  border-top: 1px solid #f2f3f5;
}
.room-card__btn {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 36px;
  font-size: 14px;
  font-weight: 600;
  border-radius: 8px;
}
.room-card__btn .van-icon {
  font-size: 15px;
}
.room-card__btn--primary {
  color: #fff;
  background: #2f6bff;
}
.room-card__btn--danger {
  color: #e34d59;
  background: #fdeef0;
}

/* 【P214】详情内图片凭证缩略图 */
.bill-img-thumb{width:72px;height:72px;object-fit:cover;border-radius:6px;border:1px solid #ebedf0;display:block;cursor:pointer;}
</style>



// WEBPACK FOOTER //
// src/components/BillList.vue